import { z } from "zod";

const ProductIdentitySchema = z
  .object({
    sku: z.string().trim().min(1).max(128),
    asin: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9]{10}$/),
  })
  .strict();
export const SmartCreationNegativeCandidatesInputSchema = z
  .object({
    products: z.array(ProductIdentitySchema).min(1).max(500),
    targetAcos: z.number().finite().min(0.01).max(2).optional(),
  })
  .strict()
  .refine(
    (value) => new Set(value.products.map((product) => product.sku)).size === value.products.length,
    { message: "Product SKUs must be unique" },
  );
export type SmartCreationNegativeCandidatesInput = z.infer<
  typeof SmartCreationNegativeCandidatesInputSchema
>;

export const SmartCreationNegativeCandidateReasonSchema = z.enum([
  "no_related_ad_groups",
  "no_history_negatives",
  "unknown_account_type",
  "unsupported_attribution_window",
  "missing_target_acos",
  "insufficient_report_coverage",
  "mixed_product_groups",
  "missing_value_reference",
  "no_qualifying_search_terms",
]);
const StatusSchema = z.enum(["enabled", "paused", "archived"]);
const HistorySchema = z
  .object({
    targetId: z.string().uuid(),
    scope: z.enum(["campaign", "ad_group"]),
    campaignId: z.string().uuid(),
    campaignName: z.string(),
    campaignStatus: StatusSchema,
    adGroupId: z.string().uuid().nullable(),
    adGroupName: z.string().nullable(),
    adGroupStatus: StatusSchema.nullable(),
    targetStatus: StatusSchema,
    syncedAt: z.string().datetime(),
  })
  .strict();
const EvidenceSchema = ProductIdentitySchema.extend({
  adGroupIds: z.array(z.string().uuid()).min(1),
  clicks: z.number().int().nonnegative(),
  spend: z.number().finite().nonnegative(),
  sales: z.number().finite().nonnegative(),
  orders: z.number().int().nonnegative(),
  spendThreshold: z.number().finite().positive(),
  referenceValue: z.number().finite().positive(),
  referenceBasis: z.enum(["product_price", "historical_aov"]),
  thresholdsMet: z
    .object({
      minimumClicks: z.literal(true),
      minimumSpend: z.literal(true),
      zeroConversions: z.literal(true),
      matureCoverage: z.literal(true),
    })
    .strict(),
}).strict();
const CandidateBase = z.object({
  id: z.string().regex(/^[a-f0-9]{64}$/),
  source: z.enum(["history_negative", "search_term_evidence"]),
  history: HistorySchema.nullable(),
  evidence: EvidenceSchema.nullable(),
});
export const SmartCreationNegativeCandidateSchema = z.discriminatedUnion("type", [
  CandidateBase.extend({
    type: z.literal("keyword"),
    text: z.string().min(1).max(80),
    matchType: z.enum(["negative_exact", "negative_phrase"]),
  }).strict(),
  CandidateBase.extend({
    type: z.literal("product"),
    asin: z.string().regex(/^[A-Z0-9]{10}$/),
  }).strict(),
]);
export type SmartCreationNegativeCandidate = z.infer<typeof SmartCreationNegativeCandidateSchema>;
export const SmartCreationNegativeCandidatesSchema = z
  .object({
    history: z
      .object({
        status: z.enum(["loaded", "no_matching_data"]),
        reasons: z.array(SmartCreationNegativeCandidateReasonSchema),
        lastSuccessfulSyncAt: z.string().datetime().nullable(),
        freshness: z.enum(["fresh", "stale", "unknown"]),
        candidates: z.array(SmartCreationNegativeCandidateSchema),
      })
      .strict(),
    recommendations: z
      .object({
        status: z.enum(["available", "no_matching_data", "insufficient_evidence"]),
        reasons: z.array(SmartCreationNegativeCandidateReasonSchema),
        candidates: z.array(SmartCreationNegativeCandidateSchema),
      })
      .strict(),
    policy: z
      .object({
        windowDays: z.literal(30),
        startDate: z.string(),
        endDate: z.string(),
        minimumClicks: z.literal(20),
        attributionDays: z.union([z.literal(7), z.literal(14)]).nullable(),
        reportLagDays: z.literal(3),
        targetAcos: z.number().finite().min(0.01).max(2).nullable(),
        spendThresholdBasis: z.literal("product_price_or_historical_aov_times_target_acos"),
        minimumSpendMultiple: z.literal(2),
      })
      .strict(),
    coverage: z
      .object({
        matchedAdGroups: z.number().int().nonnegative(),
        attributableAdGroups: z.number().int().nonnegative(),
        matureReportDays: z.number().int().nonnegative().max(30),
        totalReportDays: z.literal(30),
      })
      .strict(),
    currencyCode: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .nullable(),
  })
  .strict();
export type SmartCreationNegativeCandidates = z.infer<typeof SmartCreationNegativeCandidatesSchema>;
export const SmartCreationNegativeCandidatesResponseSchema = z
  .object({ success: z.literal(true), data: SmartCreationNegativeCandidatesSchema })
  .strict();
